import * as errors from "@superbuilders/errors"
import * as logger from "@superbuilders/slog"

import { evaluateFeedbackIdentifiers } from "../../evaluator"
import { sanitizeHtml } from "../../html"
import { parseAssessmentItemXml } from "../../parser"
import type { ValidateResult } from "../../shared/types"

export function validateResponsesFromXml(qtiXml: string, responses: Record<string, string | string[]>): ValidateResult {
	logger.debug("qti validate secure", {})
	const parseResult = errors.trySync(() => parseAssessmentItemXml(qtiXml))
	if (parseResult.error) {
		logger.error("qti parse failed during validate", { error: parseResult.error })
		throw errors.wrap(parseResult.error, "qti parse")
	}
	const item = parseResult.data
	logger.debug("qti validate: parsed item", {
		title: item.title,
		responseCount: Object.keys(responses).length
	})

	const perResponse: ValidateResult["perResponse"] = {}
	const selectedChoicesByResponse: ValidateResult["selectedChoicesByResponse"] = {}
	const responseCorrectness: Record<string, boolean> = {} // Track correctness for evaluator

	let allCorrect = true
	for (const rd of item.responseDeclarations) {
		const respId = rd.identifier
		const user = responses[respId]
		if (user == null) {
			allCorrect = false
			responseCorrectness[respId] = false // Track as incorrect
			logger.debug("qti validate: missing response", { responseId: respId })
			continue
		}
		const userVals = Array.isArray(user) ? user.filter((v): v is string => typeof v === "string") : [user]
		const correctVals = rd.correctResponse.values

		let isCorrect = false

		if (rd.cardinality === "ordered") {
			// Strict Array Equality for Order Interaction
			// Both length and specific index positions must match
			isCorrect = userVals.length === correctVals.length && userVals.every((val, index) => val === correctVals[index])
			logger.debug("qti validate: ordered check", {
				responseId: respId,
				userVals,
				correctVals,
				isCorrect
			})
		} else if (rd.baseType === "directedPair" || rd.baseType === "pair") {
			// Gap Match Interaction: Set-based comparison of pairs
			// Each pair is "sourceId gapId" format - order of pairs doesn't matter
			const correctSet = new Set(correctVals)
			const userSet = new Set(userVals)
			isCorrect = userSet.size === correctSet.size && [...userSet].every((v) => correctSet.has(v))
			logger.debug("qti validate: directedPair check", {
				responseId: respId,
				userVals,
				correctVals,
				isCorrect
			})
		} else if (rd.baseType === "float" || rd.baseType === "integer") {
			// Numeric comparison: handles "42" vs "42.0", "2.62" vs "2.620", etc.
			// This makes System B smarter than System A - we check baseType regardless of XML tag used
			const userVal = userVals[0]

			if (userVal != null) {
				const userNum = Number.parseFloat(userVal)

				if (!Number.isNaN(userNum)) {
					// Use epsilon comparison for floats, exact for integers
					const epsilon = rd.baseType === "float" ? 0.000001 : 0

					// Check if user's answer matches ANY of the correct values
					// This handles cases like: <qti-value>42</qti-value><qti-value>42.0</qti-value>
					isCorrect = correctVals.some((correctVal) => {
						const correctNum = Number.parseFloat(correctVal)
						return !Number.isNaN(correctNum) && Math.abs(userNum - correctNum) <= epsilon
					})
				}
			}

			logger.debug("qti validate: numeric check", {
				responseId: respId,
				baseType: rd.baseType,
				userVal: userVals[0],
				correctVals,
				isCorrect
			})
		} else if (rd.cardinality === "single") {
			// Single cardinality with potentially multiple acceptable answers
			// User's single answer must match ANY of the correct values
			// Example: "USA", "U.S.A.", "United States" are all acceptable
			const userVal = userVals[0]
			isCorrect = userVal != null && correctVals.includes(userVal)

			logger.debug("qti validate: single cardinality check", {
				responseId: respId,
				userVal,
				correctVals,
				isCorrect
			})
		} else {
			// Multiple cardinality: user must select ALL correct values (set equality)
			const correctSet = new Set(correctVals)
			const userSet = new Set(userVals)
			isCorrect = userSet.size === correctSet.size && [...userSet].every((v) => correctSet.has(v))

			logger.debug("qti validate: multiple cardinality check", {
				responseId: respId,
				userVals,
				correctVals,
				isCorrect
			})
		}

		if (!isCorrect) allCorrect = false
		responseCorrectness[respId] = isCorrect // Store for evaluator
		logger.debug("qti validate: evaluated response", {
			responseId: respId,
			selected: userVals,
			isCorrect
		})

		const correctSet = new Set(correctVals)
		selectedChoicesByResponse[respId] = userVals.map((id) => ({ id, isCorrect: correctSet.has(id) }))

		// Per-response feedback is now handled by the overall feedback logic.
		perResponse[respId] = { isCorrect }
	}

	// Use the evaluator to determine ALL feedback identifiers (FEEDBACK__OVERALL, FEEDBACK__PEDAGOGY, etc.)
	const feedbackIdentifiers = evaluateFeedbackIdentifiers(item, responses, responseCorrectness)
	let overallFeedback: ValidateResult["overallFeedback"]

	if (feedbackIdentifiers.length > 0) {
		// Collect all matching feedback blocks and combine their HTML
		const feedbackHtmlParts: string[] = []
		for (const feedbackId of feedbackIdentifiers) {
			const feedbackBlock = item.itemBody.feedbackBlocks.find((fb) => fb.identifier === feedbackId)
			if (feedbackBlock) {
				feedbackHtmlParts.push(sanitizeHtml(feedbackBlock.contentHtml))
			}
		}

		if (feedbackHtmlParts.length > 0) {
			overallFeedback = {
				isCorrect: allCorrect,
				messageHtml: feedbackHtmlParts.join("")
			}
			logger.debug("qti validate: overall feedback via response processing rules", {
				isCorrect: allCorrect,
				feedbackIds: feedbackIdentifiers,
				numBlocks: feedbackHtmlParts.length
			})
		}
	} else {
		// Fallback for simple items without complex processing rules
		const targetId = allCorrect ? "CORRECT" : "INCORRECT"
		const fallbackBlock = item.itemBody.feedbackBlocks.find(
			(fb) =>
				fb.identifier.toUpperCase() === targetId &&
				(fb.outcomeIdentifier === "FEEDBACK" || fb.outcomeIdentifier === "FEEDBACK__OVERALL")
		)
		if (fallbackBlock) {
			overallFeedback = { isCorrect: allCorrect, messageHtml: sanitizeHtml(fallbackBlock.contentHtml) }
			logger.debug("qti validate: overall feedback via fallback block", {
				isCorrect: allCorrect,
				blockId: fallbackBlock.identifier
			})
		}
	}

	return { overallCorrect: allCorrect, perResponse, selectedChoicesByResponse, overallFeedback }
}
